import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    Button, Row, Col, Card, CardBody, Input, Badge,
    Form, Label, Table
} from "reactstrap";
import {
    FiCreditCard, FiArrowUpRight, FiArrowDownLeft, FiRefreshCcw,
    FiUser, FiTrendingUp, FiActivity
} from "react-icons/fi";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Cell, Legend
} from "recharts";
import Select from "react-select"; // Addon machan
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";

// MUI Date Picker addon logic from ref
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const StaffWalletReport = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [rawResponse, setRawResponse] = useState(null);

    // Filters state addon with Dashboard logic structure
    const [filters, setFilters] = useState({
        staff_id: "",
        filter_period: { value: "today", label: "Today" }, // React-select object
        from_date: dayjs(), // MUI dayjs object
        to_date: dayjs()
    });

    const filterOptions = [
        { value: "all", label: "All Time Balance" },
        { value: "today", label: "Today" },
        { value: "week", label: "Last Week" },
        { value: "month", label: "Last Month" },
        { value: "3months", label: "Last 3 Months" },
        { value: "custom", label: "Custom Range" }
    ];

    const loadStaffReport = async (currentFilters = filters) => {
        dispatch(loadingTrue());
        try {
            const queryParams = new URLSearchParams({
                staff_id: currentFilters.staff_id,
                filter: currentFilters.filter_period.value,
                from_date: currentFilters.from_date ? currentFilters.from_date.format('YYYY-MM-DD') : "",
                to_date: currentFilters.to_date ? currentFilters.to_date.format('YYYY-MM-DD') : ""
            }).toString();

            const res = await Handler({ method: "GET", url: `/report/staff_report.php?${queryParams}` });
            if (res?.success) {
                setRawResponse(res);
            }
        } catch (err) {
            toast.error("Failed to sync Staff Wallet Report");
        } finally {
            dispatch(loadingFalse());
        }
    };

    useEffect(() => {
        if (filters.filter_period.value !== 'custom') {
            loadStaffReport();
        }
    }, [filters.staff_id, filters.filter_period.value]);

    const handlePeriodChange = (selectedOption) => {
        const type = selectedOption.value;
        let from = dayjs();
        let to = dayjs();

        if (type === 'week') from = dayjs().subtract(7, 'day');
        else if (type === 'month') from = dayjs().subtract(1, 'month');
        else if (type === '3months') from = dayjs().subtract(3, 'month');

        const newFilters = { ...filters, filter_period: selectedOption, from_date: from, to_date: to };
        setFilters(newFilters);

        if (type !== 'custom') {
            loadStaffReport(newFilters);
        }
    };

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            backgroundColor: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: "10px",
            minHeight: "45px",
            boxShadow: "none",
            '&:hover': { border: '1px solid #f16436' }
        })
    };

    // 🔥 Chart Logic (Logic strictly maintained)
    const chartData = useMemo(() => {
        const items = rawResponse?.data || [];
        return items.map(item => ({
            name: item?.staff?.name?.split(' ')[0] || 'Staff',
            deposit: item?.total_deposit || 0,
            expense: item?.total_expense || 0,
            balance: item?.balance || 0
        }));
    }, [rawResponse]);

    const reports = rawResponse?.data || [];
    const summary = rawResponse?.summary || {};

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-container p-3 bg-light min-vh-100">
                {/* Header addon from ref */}
                <div className="d-md-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h3 className="fw-bold m-0 text-dark">Staff Wallet Analytics</h3>
                        <p className="text-muted small m-0">Live deposit & expense tracking.</p>
                    </div>
                    <div className="d-flex gap-2 mt-3 mt-md-0 align-items-center">
                        <div style={{ width: '180px' }}>
                            <Select
                                options={filterOptions}
                                value={filters.filter_period}
                                onChange={handlePeriodChange}
                                styles={customSelectStyles}
                            />
                        </div>
                        <Button color="white" className="shadow-sm border rounded-pill px-3 py-2" onClick={() => loadStaffReport()}>
                            <FiRefreshCcw />
                        </Button>
                        <Button color="dark" className="px-4 shadow-sm fw-bold rounded-pill" onClick={() => navigate("/staffs")}>
                            View Staffs
                        </Button>
                    </div>
                </div>

                {/* MUI Custom Range Filter Addon */}
                {filters.filter_period.value === 'custom' && (
                    <Card className="border-0 shadow-sm rounded-4 mb-4 animate__animated animate__fadeIn">
                        <CardBody className="p-4">
                            <Row className="g-3 align-items-end">
                                <Col md={4}>
                                    <Label className="x-small fw-bold text-muted d-block mb-2 text-uppercase">From Date</Label>
                                    <DatePicker
                                        disableFuture
                                        format="DD/MM/YYYY"
                                        value={filters.from_date}
                                        maxDate={dayjs()}
                                        onChange={(val) => setFilters({ ...filters, from_date: val })}
                                        sx={{ width: '100%', '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: '#fff' } }}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label className="x-small fw-bold text-muted d-block mb-2 text-uppercase">To Date</Label>
                                    <DatePicker
                                        disableFuture
                                        format="DD/MM/YYYY"
                                        value={filters.to_date}
                                        minDate={filters.from_date}
                                        maxDate={dayjs()}
                                        onChange={(val) => setFilters({ ...filters, to_date: val })}
                                        sx={{ width: '100%', '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: '#fff' } }}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Button color="primary" block className="fw-bold rounded-3 shadow-sm" style={{ height: '54px' }} onClick={() => loadStaffReport()}>
                                        Sync Wallet Data
                                    </Button>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                )}

                {/* Summary Cards (Maintained) */}
                <Row className="g-3 mb-4">
                    <Col lg={4}>
                        <StatCard title="Total Deposits" value={`₹${summary?.total_deposit?.toLocaleString() || 0}`} icon={<FiArrowUpRight />} color="#0d6efd" bg="#0d6efd15" />
                    </Col>
                    <Col lg={4}>
                        <StatCard title="Total Expenses" value={`₹${summary?.total_expense?.toLocaleString() || 0}`} icon={<FiArrowDownLeft />} color="#dc3545" bg="#dc354515" />
                    </Col>
                    <Col lg={4}>
                        <StatCard title="Net Balance" value={`₹${summary?.balance?.toLocaleString() || 0}`} icon={<FiCreditCard />} color="#198754" bg="#19875415" />
                    </Col>
                </Row>

                {/* Visual Analytics (Maintained) */}
                <Card className="border-0 shadow-sm rounded-4 overflow-hidden mb-4">
                    <CardBody className="p-4">
                        <h6 className="fw-bold mb-4 text-muted small text-uppercase ls-1">Staff Expense vs Deposit Comparison</h6>
                        <div style={{ width: '100%', height: 320 }}>
                            {chartData.length > 0 ? (
                                <ResponsiveContainer>
                                    <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                        <XAxis dataKey="name" axisLine={false} tickLine={false} fontSize={11} />
                                        <YAxis axisLine={false} tickLine={false} fontSize={11} />
                                        <Tooltip cursor={{ fill: '#f8f9fa' }} contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                        <Legend verticalAlign="top" align="right" height={36} iconType="circle" />
                                        <Bar name="Deposit" dataKey="deposit" fill="#0d6efd" radius={[4, 4, 0, 0]} barSize={25} />
                                        <Bar name="Expense" dataKey="expense" fill="#dc3545" radius={[4, 4, 0, 0]} barSize={25} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <div className="h-100 d-flex align-items-center justify-content-center text-muted">No data available for visualization</div>
                            )}
                        </div>
                    </CardBody>
                </Card>

                {/* Detailed Ledger Table (Maintained) */}
                <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                    <div className="bg-white p-3 border-bottom d-flex align-items-center justify-content-between">
                        <h6 className="m-0 fw-bold">Staff Ledger Summary</h6>
                        <Badge color="soft-primary" className="text-primary">Live Wallet Status</Badge>
                    </div>
                    <CardBody className="p-0">
                        <Table responsive hover className="mb-0 align-middle">
                            <thead className="bg-light">
                                <tr className="text-muted small text-uppercase ls-1">
                                    <th className="ps-4 py-3">Staff Name</th>
                                    <th className="text-center">Total Deposit</th>
                                    <th className="text-center">Total Expense</th>
                                    <th className="text-end pe-4">Available Balance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reports?.length > 0 ? (
                                    reports.map((item) => (
                                        <tr key={item?.staff?.id}>
                                            <td className="ps-4 py-3">
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="bg-light rounded-circle d-flex align-items-center justify-content-center" style={{ width: '35px', height: '35px' }}>
                                                        <FiUser className="text-muted" />
                                                    </div>
                                                    <div className="fw-bold text-dark text-uppercase small">{item?.staff?.name}</div>
                                                </div>
                                            </td>
                                            <td className="text-center text-primary fw-bold small">₹{item?.total_deposit?.toLocaleString()}</td>
                                            <td className="text-center text-danger fw-bold small">₹{item?.total_expense?.toLocaleString()}</td>
                                            <td className="text-end pe-4">
                                                <div className={`fw-bold fs-6 ${item?.balance > 0 ? 'text-success' : item?.balance < 0 ? 'text-danger' : 'text-muted'}`}>
                                                    ₹{item?.balance?.toLocaleString()}
                                                </div>
                                                <small className="text-muted x-small">Closing Balance</small>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="4" className="py-5 text-center text-muted italic">No staff records found.</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </CardBody>
                </Card>
            </div>
        </LocalizationProvider>
    );
};

const StatCard = ({ title, value, icon, color, bg }) => (
    <Card className="border-0 shadow-sm rounded-4 h-100 bg-white">
        <CardBody className="d-flex align-items-center gap-3 p-4">
            <div style={{
                width: '50px', height: '50px', borderRadius: '15px',
                backgroundColor: bg, color: color,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px'
            }}>
                {icon}
            </div>
            <div>
                <p className="m-0 text-muted x-small fw-bold text-uppercase ls-1">{title}</p>
                <h4 className="m-0 fw-bold" style={{ color: '#2c3e50' }}>{value}</h4>
            </div>
        </CardBody>
    </Card>
);

export default StaffWalletReport;